/**
 * Member row (AUTONOMY · Layer 1): besides removing, allows CHANGING the
 * role (dialog with descriptions) and REASSIGNING the campus — full member
 * management without a developer. The backend protects the last owner.
 */
import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { MapPin, Shield, Trash2, User as UserIcon } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Select } from "@/components/ui/select";
import { useI18n } from "@/providers/i18n-provider";

interface Member {
  id: string;
  role: string;
  campusId?: string | null;
  userId: string;
  user: {
    name: string | null;
    /** D3: `null` = redacted by capability, not "no email". */
    email?: string | null;
  };
}

interface CampusOption {
  id: string;
  name: string;
}

interface OrganizationMemberRowProps {
  member: Member;
  campuses?: CampusOption[];
  /** Can the actor manage members (owner/org_admin)? */
  canManage?: boolean;
  /** Is the actor an owner? (allows offering the owner role). */
  actorIsOwner?: boolean;
  onRemove: (memberId: string) => void;
  /**
   * Requests opening the role-change dialog for THIS member. The dialog
   * lives on the PAGE (a single instance): rows are remounted on every
   * refetch of the members query and a local dialog would die instantly.
   */
  onRequestChangeRole?: (member: Member) => void;
  onChangeCampus?: (memberId: string, campusId: string | null) => Promise<void>;
}

const NO_CAMPUS = "__all__";

export function OrganizationMemberRow(props: OrganizationMemberRowProps) {
  const { t } = useI18n();

  const campusName = () =>
    props.campuses?.find((c) => c.id === props.member.campusId)?.name ?? t(msg`All campuses`);

  // D3: `user.email` is `null` when the server redacts it by capability (a
  // student doesn't see the academy's emails). The testid and visible name
  // fall back to userId so as not to depend on data that may not arrive.
  const handle = () => props.member.user.email ?? props.member.userId;

  const canEditRole = () =>
    Boolean(props.canManage && props.onRequestChangeRole) &&
    // Touching an owner requires being an owner (transfer/demotion).
    (props.member.role !== "owner" || props.actorIsOwner);

  return (
    <div
      class="group flex flex-col gap-2 rounded-lg border border-transparent p-2 hover:bg-muted/50 transition-colors"
      data-testid={`member-row-${handle()}`}
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3 min-w-0">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <UserIcon class="h-4 w-4" />
          </div>
          <div class="min-w-0">
            <p class="truncate text-sm font-medium">{props.member.user.name || handle()}</p>
            <p class="truncate text-xs text-muted-foreground">
              <span class="capitalize">{props.member.role.replace("_", " ")}</span>
              <Show when={props.campuses?.length}>
                {" · "}
                <MapPin class="inline h-3 w-3 align-[-2px]" /> {campusName()}
              </Show>
            </p>
          </div>
        </div>
        <div class="flex items-center gap-1">
          <Show when={canEditRole()}>
            <Button
              variant="ghost"
              size="icon"
              class="h-8 w-8 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label={t(msg`Change role`)}
              data-testid={`member-change-role-${handle()}`}
              onClick={() => props.onRequestChangeRole?.(props.member)}
            >
              <Shield class="h-4 w-4" />
            </Button>
          </Show>
          <Show when={props.member.role !== "owner" && props.canManage !== false}>
            <ConfirmDialog
              title={t(msg`Remove member?`)}
              description={t(msg`Are you sure you want to remove this user from the organization?`)}
              confirmLabel={t(msg`Remove`)}
              variant="destructive"
              onConfirm={() => {
                props.onRemove(props.member.id);
              }}
              renderTrigger={(open) => (
                <Button
                  variant="ghost"
                  size="icon"
                  class="h-8 w-8 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={open}
                >
                  <Trash2 class="h-4 w-4" />
                </Button>
              )}
            />
          </Show>
        </div>
      </div>

      {/* AUTONOMY: member's campus (campus_admin/teacher/staff scoping). */}
      <Show when={props.canManage && props.onChangeCampus && (props.campuses?.length ?? 0) > 0}>
        <div class="pl-12">
          <Select
            aria-label={t(msg`Campus`)}
            value={props.member.campusId ?? NO_CAMPUS}
            onChange={(value) => {
              void props.onChangeCampus?.(props.member.id, value === NO_CAMPUS ? null : value);
            }}
            options={[
              { value: NO_CAMPUS, label: t(msg`All campuses`) },
              ...(props.campuses ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
        </div>
      </Show>
    </div>
  );
}
